import {
  Component,
  HostListener,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { LoaderComponent } from '../loader/loader.component';
import { Router } from '@angular/router';
import { SearchChatViewService } from '@core/services/search-chat-view.service';
import {
  ResponsiveService,
  ScreenSize,
} from '@core/services/responsive.service';
import { ChatService } from '@core/services/chat.service';
import { ChatStateService } from '@core/services/chat-state.service';
import { SidebarService } from '@core/services/side-bar.service';
import { NavbarViewManagerService } from '@core/services/navbar-view-manager.service';
import { faXmark, faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

@Component({
  selector: 'app-search-chat',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, LoaderComponent],
  templateUrl: './search-chat.component.html',
  styleUrl: './search-chat.component.css',
})
export class SearchChatComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  faXmark = faXmark;
  faSparkles = faSparkles;
  isOpen = false;
  isMobile = false;
  currentScreenSize: ScreenSize = ScreenSize.LG;

  searchTerm = '';
  searchResults: any[] = [];
  recentChats: any[] = [];
  todayChats: any[] = [];
  yesterdayChats: any[] = [];
  isLoading = false;
  isLoadingInitial = false;
  showRecentChats = false;
  private hasInitialChatsLoaded = false;
  private searchSubject = new Subject<string>();
  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private searchChatViewService: SearchChatViewService,
    private responsiveService: ResponsiveService,
    private chatService: ChatService,
    private chatState: ChatStateService,
    private sidebarService: SidebarService,
    private navbarViewManagerService: NavbarViewManagerService
  ) {
    this.subscriptions.push(
      this.searchChatViewService.isOpen$.subscribe((state) => {
        this.isOpen = state;
        if (state) {
          this.searchTerm = '';
          this.searchResults = [];
          this.searchSubject.next('');
          this.loadInitialChats();
          setTimeout(() => this.focusSearchInput(), 100);
        }
      })
    );

    this.subscriptions.push(
      this.chatState.refreshChats$.subscribe(() => {
        this.invalidateInitialChatsCache();
        // Si el modal está abierto, refrescar inmediatamente la lista para que
        // los nuevos chats aparezcan en el historial sin perderse.
        if (this.isOpen) {
          if (this.searchTerm && this.searchTerm.length > 1) {
            this.performSearch(this.searchTerm);
          } else {
            this.loadInitialChats();
          }
        }
      })
    );

    this.subscriptions.push(
      this.chatState.messageSent$.subscribe((sent) => {
        // Invalidar caché de chats cuando se envía un mensaje para que aparezca inmediatamente
        if (sent) {
          this.invalidateInitialChatsCache();
        }
      })
    );

    this.responsiveService.isMobile$.subscribe((mobile) => {
      this.isMobile = mobile;
    });

    this.responsiveService.currentScreenSize$.subscribe((size) => {
      this.currentScreenSize = size;
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.searchSubject
        .pipe(
          debounceTime(300),
          filter((term) => term.length > 1)
        )
        .subscribe((term) => {
          this.performSearch(term);
        })
    );
  }

  ngAfterViewInit(): void {
    if (this.isOpen) {
      this.focusSearchInput();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Indica si un chat concreto tiene una respuesta en curso de procesamiento.
   * Se apoya en el estado global de streams activos.
   */
  isChatProcessing(chat: any): boolean {
    const chatId = (chat?.chat_id || chat?.chat_uuid || chat?.uuid || chat?.id)?.toString?.();
    if (!chatId) {
      return false;
    }
    return this.chatState.hasActiveStreamForChat(chatId);
  }

  onSearchInput(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
    if (this.searchTerm) {
      this.searchSubject.next(this.searchTerm);
    } else {
      this.searchResults = [];
    }
  }

  loadInitialChats(): void {
    if (
      this.hasInitialChatsLoaded &&
      (this.todayChats.length > 0 || this.yesterdayChats.length > 0)
    ) {
      return;
    }

    this.isLoadingInitial = true;
    this.todayChats = [];
    this.yesterdayChats = [];

    this.chatService.getChats(1).subscribe({
      next: (response) => {
        const chats = response.data || [];
        this.groupInitialChatsByDate(chats);
        this.hasInitialChatsLoaded = true;
        this.isLoadingInitial = false;
      },
      error: (error) => {
        console.error('Error al cargar chats iniciales:', error);
        this.isLoadingInitial = false;
      },
    });
  }

  private invalidateInitialChatsCache(): void {
    this.hasInitialChatsLoaded = false;
    this.todayChats = [];
    this.yesterdayChats = [];
  }

  private getDateGroup(date: string | Date): string {
    const chatDate = new Date(date);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (chatDate >= today) {
      return 'today';
    } else if (chatDate >= yesterday) {
      return 'yesterday';
    } else {
      return 'older';
    }
  }

  private groupInitialChatsByDate(chats: any[]): void {
    chats.forEach((chat) => {
      if (this.getFirstUserLineText(chat) && chat.created_at) {
        const group = this.getDateGroup(chat.created_at);

        const chatWithIcon = {
          ...chat,
          parsedIcon: this.getParsedIcon(chat),
        };

        if (group === 'today') {
          this.todayChats.push(chatWithIcon);
        } else if (group === 'yesterday') {
          this.yesterdayChats.push(chatWithIcon);
        }
      }
    });

    this.todayChats.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    this.yesterdayChats.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }

  getChatTitle(chat: any): string {
    const title = this.getFirstUserLineText(chat);
    return title || 'Chat sin título';
  }

  private getFirstUserLineText(chat: any): string {
    const firstUserLine = chat?.first_user_line;
    const text =
      firstUserLine?.display_content ??
      firstUserLine?.content ??
      firstUserLine?.message ??
      '';
    return typeof text === 'string' ? text.trim() : '';
  }

  getParsedIcon(chat: any): any {
    const iconData = chat?.agent?.icon ?? chat?.agent_datasource?.icon;
    if (!iconData) return this.faSparkles;

    try {
      const iconParsed = typeof iconData === 'string' ? JSON.parse(iconData) : iconData;
      const style = iconParsed?.style;
      const name = iconParsed?.name;
      if (style && name) return [style, name];
    } catch {
      // icon data is malformed
    }

    return this.faSparkles;
  }

  performSearch(term: string): void {
    this.isLoading = true;

    this.chatService.searchChats(term).subscribe({
      next: (response) => {
        this.searchResults = response.data || [];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error al buscar chats:', error);
        this.isLoading = false;
      },
    });
  }

  selectChat(chat: any): void {
    this.navbarViewManagerService.closeAllNavbarViews();

    this.chatState.clearMessages();

    const chatId = (chat?.chat_id || chat?.chat_uuid || chat?.uuid || chat?.id)?.toString?.();
    if (!chatId) {
      return;
    }
    localStorage.setItem('chat_id', chatId);
    this.chatState.setChatId(chatId, true);

    this.closeModal();

    this.router.navigate(['/dashboard/chat']);

    if (this.isMobile && this.sidebarService.isOpenValue) {
      this.sidebarService.toggleSidebar();
    }
  }

  focusSearchInput(): void {
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
  }

  closeModal() {
    this.searchChatViewService.toggleSearchChatModal();
  }

  isSmallScreen(): boolean {
    return (
      this.isMobile ||
      this.currentScreenSize === ScreenSize.XS ||
      this.currentScreenSize === ScreenSize.SM
    );
  }

  @HostListener('document:click')
  closeAllMenus() {}
}
